/* This example requires Tailwind CSS v2.0+ */
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { insertTimestampIntoText } from "../../../helpers/getTimestamp";
import { updateClientServiceStatus } from "../../../services/clientService/updateClientServiceStatus";
import { getSubscriptionTeamMembers } from "../../../services/clientServiceSubscription/getSubscriptionTeamMembers";
import { getClientServiceSubscriptionTasksByClientServiceId } from "../../../services/clientServiceSubscriptionTask/getClientServiceSubscriptionTasksByServiceId";
import { updateTaskService } from "../../../services/clientServiceSubscriptionTask/updateTaskService";
import { getClientServiceSubscriptionTaskChecklistItemByTaskId } from "../../../services/clientServiceSubscriptionTaskChecklistItem/getClientServiceSubscriptionTaskChecklistItemByTaskId";
import { updateClientServiceSubscriptionTaskChecklistItemCheck } from "../../../services/clientServiceSubscriptionTaskChecklistItem/updateClientServiceSubscriptionTaskChecklistItemCheck";
import { term } from "../../../terms";
import ButtonAddTimestamp from "../../../utils/ButtonAddTimestamp";
import InputTextModal from "../modalGeneralComponents/InputTextModal";
import AssignedByListBox from "./AssignedByListBox";
import ButtonModal from "./ButtonModal";
import ErrorMessage from "./ErrorMessage";
import InputDateModal from "./InputDateModal";
import InputTextAreaModal from "./InputTextAreaModal";
import TaskRenewalCheckBox from "./TaskRenewalCheckBox";
import { usePermissions } from "../../../context/PermissionsContext";
import { getClientServiceMessage } from "../../../helpers/clientServiceMessage";

const UpdateClientServiceSubscriptionTaskModal = ({
    open,
    setOpen,
    updateTaskModal,
    setUpdateTaskModal,
    loggedInEmployee,
    updateTasks,
    clientServiceId,
    clientService,
    updateClientService,
    initializeJobs,
    clientId,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteSteps = can("steps.write");
    const canWriteServices = can("client_services.write");
    const [displayNotes, setDisplayNotes] = useState(false);
    const [errors, setErrors] = useState({});
    const statuses = ["To Do", "In Progress", "Complete"];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [teamMembers, setTeamMembers] = useState(null);

    const completeButtonRef = useRef(null);
    const notesRef = useRef(null);

    useEffect(() => {
        if (permissionsLoading || !canWriteSteps || !clientServiceId) return;
        const getTeamMembers = async () => {
            const response = await getSubscriptionTeamMembers(clientServiceId);
            console.log("team-members", response);
            setTeamMembers(response.data.teamMembers);
        };
        getTeamMembers();
    }, [clientServiceId, open, canWriteSteps, permissionsLoading]);

    const toggleNotes = () => {
        setDisplayNotes(!displayNotes);
        setErrors({});
    };

    const onHandleCancel = () => {
        setErrors({});
        setOpen(false);
    };

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        if (
            (name === "dueDate" && value < updateTaskModal.assignedDate) ||
            (name === "assignedDate" && value > updateTaskModal.dueDate)
        ) {
            toast.error("Due date cannot be less than assigned date.");
            return;
        }
        setUpdateTaskModal({ ...updateTaskModal, [name]: value });
    };

    const onHandleChangeAssignedEmployees = (newAssigned) => {
        setUpdateTaskModal({
            ...updateTaskModal,
            assignedEmployees: [newAssigned],
        });
    };

    const onHandleClickAddTimestamp = () => {
        const textarea = notesRef.current;
        if (!textarea) return;

        const current = updateTaskModal.notes || "";

        // If textarea is not focused, default to "append at the end"
        if (document.activeElement !== textarea) {
            textarea.focus();
            const endPos = current.length;
            textarea.setSelectionRange(endPos, endPos);
        }

        const start =
            typeof textarea.selectionStart === "number"
                ? textarea.selectionStart
                : current.length;
        const end =
            typeof textarea.selectionEnd === "number"
                ? textarea.selectionEnd
                : start;

        const { caretPosition, value: updated } = insertTimestampIntoText(
            current,
            start,
            end,
            loggedInEmployee,
        );

        setUpdateTaskModal((prev) => ({ ...prev, notes: updated }));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(caretPosition, caretPosition);
            });
        });
    };

    const validateForm = (form) => {
        let errs = {};
        if (form.status === 2) {
            ["name", "assignedEmployees", "assignedDate", "dueDate"].forEach(
                (field) => {
                    if (
                        !form[field] ||
                        (Array.isArray(form[field]) && form[field].length === 0)
                    ) {
                        errs[field] = "This field cannot be empty.";
                    }
                },
            );
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onUnstartJob = async () => {
        if (permissionsLoading || !canWriteSteps || isSubmitting) return;

        try {
            // 1. Fetch checklist items for this job
            const checklistRes =
                await getClientServiceSubscriptionTaskChecklistItemByTaskId(
                    updateTaskModal.id,
                );
            const items = checklistRes.data.checklistItemsResponse;
            // 2. Uncheck all items
            await Promise.all(
                items.map((item) =>
                    updateClientServiceSubscriptionTaskChecklistItemCheck(
                        item.id,
                        false,
                    ),
                ),
            );
            toast.success("All checklist items have been unchecked");

            // 3. Reset job status to 'To Do'
            const jobPayload = {
                ...updateTaskModal,
                status: 0,
                assignedByEmployee: null,
                assignedEmployees: [],
                assignedDate: "0001-01-01T00:00:00",
                completedDate: "0001-01-01T00:00:00",
                completedBy: null,
            };
            const jobRes = await updateTaskService(
                updateTaskModal.id,
                jobPayload,
                loggedInEmployee.employeeId,
            );
            if (!jobRes.data.success) {
                toast.error(jobRes.data.message || "Failed to reset step");
                return;
            }
            toast.success("Step has been reset to To Do");

            // 4. Reload tasks to check subscription
            const { data: fresh } =
                await getClientServiceSubscriptionTasksByClientServiceId(
                    clientServiceId,
                );
            const allTasks = fresh.tasksResponse;
            const anyInProgressOrComplete = allTasks.some(
                (t) => t.status === 1 || t.status === 2,
            );

            // 5. Reset subscription if no job In Progress
            if (!anyInProgressOrComplete && canWriteServices) {
                const subRes = await updateClientServiceStatus(
                    clientServiceId,
                    0,
                    jobPayload,
                    loggedInEmployee,
                );
                if (subRes.success) {
                    toast.success("Service status reset to To Do");
                    // Update client service state immediately in UI
                    updateClientService({ ...clientService, status: 0 });
                } else {
                    toast.error(
                        getClientServiceMessage(
                            subRes.message,
                            "Failed to reset service",
                        ),
                    );
                }
            }

            // 6. Notify parent of updated job and reset job assignment state
            updateTasks(jobPayload);
            initializeJobs();
            // 7. Close modal
            setOpen(false);
        } catch (error) {
            console.error("Error in onUnstartJob:", error);
            toast.error("Error while unstarting item and resetting checklist");
        }
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteSteps || isSubmitting) return;
        if (!validateForm(updateTaskModal)) return;
        setIsSubmitting(true);

        const payload = { ...updateTaskModal, assignedByEmployee: loggedInEmployee };
        const resp = await updateTaskService(
            updateTaskModal.id,
            payload,
            loggedInEmployee.employeeId,
        );
        if (resp.data.success) {
            toast.success(resp.data.message);
            updateTasks(payload);
            setOpen(false);
        } else {
            toast.error(resp.data.message);
        }

        setIsSubmitting(false);
    };

    const validateAssignedEmployee = (assigned) =>
        Array.isArray(assigned) ? assigned[0] : assigned;

    const formatDate = (date) =>
        date && date !== "0001-01-01T00:00:00"
            ? date.split("T")[0]
            : new Date().toISOString().split("T")[0];

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" />
                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full max-w-lg p-6 bg-white rounded-lg shadow-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <Dialog.Title className="text-lg font-medium text-gray-900">
                                        Update {term("step_singular")}
                                    </Dialog.Title>
                                    {(updateTaskModal.status === 1 ||
                                        updateTaskModal.status === 2) &&
                                        loggedInEmployee.role == 1 && (
                                            <button
                                                onClick={onUnstartJob}
                                                className="px-3 py-1 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600"
                                            >
                                                Unstart {term("step_singular")}
                                            </button>
                                        )}
                                </div>
                                <form
                                    onSubmit={onHandleSubmit}
                                    className="space-y-5"
                                >
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Status
                                        </label>
                                        <span
                                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${
                                                statuses[updateTaskModal.status] ===
                                                "Complete"
                                                    ? "bg-green-500"
                                                    : statuses[
                                                            updateTaskModal.status
                                                        ] === "In Progress"
                                                      ? "bg-yellow-500"
                                                      : "bg-red-500"
                                            }
                    `}
                                        >
                                            {statuses[updateTaskModal.status]}
                                        </span>
                                    </div>

                                    <InputTextModal
                                        required
                                        label="Name"
                                        name="name"
                                        value={updateTaskModal.name}
                                        onChange={onHandleChange}
                                        disabled
                                    />
                                    {errors.name && (
                                        <ErrorMessage error={errors.name} />
                                    )}

                                    {updateTaskModal.status === 2 && (
                                        <TaskRenewalCheckBox
                                            name="taskRenewalState"
                                            checked={
                                                updateTaskModal.taskRenewalState
                                            }
                                            onChange={(e) =>
                                                setUpdateTaskModal({
                                                    ...updateTaskModal,
                                                    taskRenewalState: e.target
                                                        .checked
                                                        ? 1
                                                        : 0,
                                                })
                                            }
                                        />
                                    )}

                                    <AssignedByListBox
                                        label="Assigned Employee"
                                        employees={teamMembers}
                                        assignedBy={validateAssignedEmployee(
                                            updateTaskModal.assignedEmployees,
                                        )}
                                        onChange={onHandleChangeAssignedEmployees}
                                    />
                                    {errors.assignedEmployees && (
                                        <ErrorMessage
                                            error={errors.assignedEmployees}
                                        />
                                    )}

                                    <InputDateModal
                                        label="Due Date"
                                        name="dueDate"
                                        value={formatDate(updateTaskModal.dueDate)}
                                        onChange={onHandleChange}
                                    />
                                    {errors.dueDate && (
                                        <ErrorMessage error={errors.dueDate} />
                                    )}

                                    {!displayNotes ? (
                                        <button
                                            type="button"
                                            onClick={toggleNotes}
                                            className="inline-flex items-center p-1 mt-2 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                        >
                                            Add notes
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={toggleNotes}
                                            className="inline-flex items-center p-1 mt-2 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        >
                                            Hide notes
                                        </button>
                                    )}

                                    {displayNotes && (
                                        <>
                                            <InputTextAreaModal
                                                ref={notesRef}
                                                label="Notes"
                                                name="notes"
                                                onChange={onHandleChange}
                                                value={updateTaskModal.notes || ""}
                                            />
                                            <ButtonAddTimestamp
                                                onHandleClick={
                                                    onHandleClickAddTimestamp
                                                }
                                            />
                                        </>
                                    )}

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name={
                                                isSubmitting ? "Saving..." : "Save"
                                            }
                                            type="submit"
                                            ref={completeButtonRef}
                                            disabled={
                                                isSubmitting ||
                                                permissionsLoading ||
                                                !canWriteSteps
                                            }
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:col-start-2 sm:text-sm disabled:opacity-50"
                                        />

                                        <ButtonModal
                                            name="Cancel"
                                            type="button"
                                            onClick={onHandleCancel}
                                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        />
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default UpdateClientServiceSubscriptionTaskModal;
